import { useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import styled from "styled-components";
import { useAuth } from "../../../../contexts/UserContext";
import { IInterrupcaoProfissional } from "../../../../utility/IInterrupcaoProfissional";
import {
  Cartao,
  Titulo,
  Conteudo,
  Rotulo,
  ValorDestacado,
  Arquivo,
  ArquivoIcone,
  ArquivoConteudo,
  ArquivoNome,
  ArquivoAcao,
  Timestamp
} from "../styles";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { IconWrong, IconUser } from "../../../icons";
import { IconDocumentUpload, IconExternalLink, IconClock } from "../../../icons/IconesSistema";
import { IconArrowDown, IconArrowUp } from "../../../icons/index";
import { comum } from "../../../../styles/Themes/light";

const Contagem = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 27px;
  min-width: 16px;
  line-height: 18px;
  padding: 4px;
  font-size: 11px;
  font-weight: 600;
  background-color: ${({ theme }) => theme.colors.interfaceGray300};
  color: ${(props: any) => props.theme.colors.pureWhite};
  border-radius: 4px;
  text-align: center;
`;

const BotaoExpandir = styled.button`
  margin-left: auto;
  background: transparent;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
`;

const ParagrafoSemInterrupcao = styled.p`
  margin: 8px 0 0;
  color: #64748b;
  font-size: 14px;
`;

const HeaderInterrupcao = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const AvatarContainer = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background-color: ${({ theme }) => theme.colors.interfacePure0 || '#FFFFFF'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 0 2px 0 rgba(0, 0, 0, 0.1);
`;

const InfoContainer = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: space-between;
`;

const NomeProfissional = styled.strong`
  font-weight: 600;
  color: #020817;
  font-size: 14px;
  line-height: 20px;
`;

const CodigoProfissional = styled.span`
  color: #64748b;
  font-size: 11px;
  font-weight: 400;
  line-height: 16px;
`;

const LinhaInterrupcao = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 12px;
`;

const formatarDataRelativa = (data: string | Date | null | undefined): string => {
  if (!data) return "";
  try {
    const dataFormatada = new Date(data);
    if (Number.isNaN(dataFormatada.getTime())) return "";
    return formatDistanceToNow(dataFormatada, { addSuffix: true, locale: ptBR });
  } catch (error) {
    return "";
  }
};

interface Props {
  demanda: any;
  loading?: boolean;
  onNavigateToFile?: (idArquivo: number) => void;
}

export default function InterrupcoesProfissionais({ demanda, loading = false, onNavigateToFile }: Props) {
  const { user } = useAuth();
  const history = useHistory();
  const { id: idDemanda } = useParams<{ id: string }>();

  const [aberto, setAberto] = useState(false);
  const podeVer = user?.profile === 'admin' || user?.profile === 'operador';

  const interrupcoes: IInterrupcaoProfissional[] = useMemo(
    () => (demanda?.interrupcoes_profissionais as IInterrupcaoProfissional[]) ?? [],
    [demanda?.interrupcoes_profissionais]
  );

  const alternar = () => setAberto((visivel) => !visivel);

  const handleArquivoClick = (arquivo: any) => {
    if (onNavigateToFile && arquivo?.id_proposta_arquivo) {
      onNavigateToFile(arquivo.id_proposta_arquivo);
    } else if (idDemanda) {
      history.push(`/demandas/${idDemanda}/anexos`);
    }
  };

  if (!podeVer) {
    return null;
  }

  if (loading) {
    return (
      <Cartao>
        <Titulo>
          <Skeleton circle width={18} height={18} />
          <Skeleton width={200} height={18} />
          <div style={{ marginLeft: 'auto' }}>
            <Skeleton width={24} height={18} />
          </div>
        </Titulo>
        <Conteudo>
          <Skeleton width="100%" height={64} borderRadius={10} />
        </Conteudo>
      </Cartao>
    );
  }

  if (interrupcoes.length === 0) {
    return null;
  }

  return (
    <Cartao>
      <Titulo>
        <IconWrong />
        Interrupções do profissional
        <Contagem>{interrupcoes.length}</Contagem>
        <BotaoExpandir onClick={alternar}>
          {aberto ? <IconArrowUp /> : <IconArrowDown />}
        </BotaoExpandir>
      </Titulo>

      {aberto && (
        <Conteudo>
          {interrupcoes.map((interrupcao) => {
              const nomeCompleto = (interrupcao as any)?.usuario?.nome || "";
              const codigoFormatado = interrupcao.id_usuario ? `D${interrupcao.id_usuario}` : "";
              const dataRelativa = formatarDataRelativa(interrupcao.criado || interrupcao.modificado);
              const arquivos = interrupcao.arquivos || [];
              const temIdentificacao = nomeCompleto || codigoFormatado;

              return (
                <LinhaInterrupcao key={interrupcao.id_interrupcao_profissional}>
                  {temIdentificacao && (
                    <HeaderInterrupcao>
                      <AvatarContainer>
                        <IconUser color={comum.colors.primaryRed400} size={20} />
                      </AvatarContainer>
                      <InfoContainer>
                        {nomeCompleto ? (
                          <>
                            <NomeProfissional>{nomeCompleto}</NomeProfissional>
                            {codigoFormatado && <CodigoProfissional>{codigoFormatado}</CodigoProfissional>}
                          </>
                        ) : codigoFormatado ? (
                          <NomeProfissional>{codigoFormatado}</NomeProfissional>
                        ) : null}
                        {dataRelativa && (
                          <Timestamp style={{ marginLeft: 'auto' }}>
                            <IconClock size={14} />
                            {dataRelativa}
                          </Timestamp>
                        )}
                      </InfoContainer>
                    </HeaderInterrupcao>
                  )}

                  <Rotulo>Motivo da interrupção:</Rotulo>
                  <ValorDestacado>{interrupcao.justificativa || "--"}</ValorDestacado>

                  {arquivos.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <Rotulo>Anexos relacionados:</Rotulo>
                      <ValorDestacado>
                        {arquivos.map((arquivo: any, index: number) => (
                          <Arquivo key={arquivo.id_proposta_arquivo || index} onClick={() => handleArquivoClick(arquivo)}>
                            <ArquivoIcone>
                              <IconDocumentUpload size={16} color="#64748b" />
                            </ArquivoIcone>
                            <ArquivoConteudo>
                              <ArquivoNome>{arquivo?.caminho?.split("/").pop() || "--"}</ArquivoNome>
                            </ArquivoConteudo>
                            <ArquivoAcao>
                              <IconExternalLink size={16} color="var(--c353a40)" />
                            </ArquivoAcao>
                          </Arquivo>
                        ))}
                      </ValorDestacado>
                    </div>
                  )}
                </LinhaInterrupcao>
              );
            })}
        </Conteudo>
      )}
    </Cartao>
  );
}

